import { NoData, Pending, PhoneNumber, sharpDateFormatter } from '@finbeat/react-toolkit';
import DateTimeText from 'components/Common/DataTime';
import { ClientHistory } from 'models/api/client';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Container,
    Table
} from 'reactstrap';

type ClientChangeModalHistoryProps = {
    clientId: number | string;
    isShown: boolean;
    collectionStatus: string | null;
    query: () => Promise<ClientHistory>;
};

const HistoryTab = ({ clientId, isShown, collectionStatus, query }: ClientChangeModalHistoryProps) => {
    useTranslation();
    const { dateFormat } = useGeneralSettingsContext();

    const [history, setHistory] = useState<ClientHistory | undefined>();
    const [queryEnable, setQueryEnable] = useState(isShown);

    useEffect(() => {
        isShown && setQueryEnable(true);
    }, [isShown]);

    useQuery('clientHistory' + clientId, query, {
        onSuccess: (res) => setHistory(res),
        enabled: queryEnable,
    });

    const [open, setOpen] = useState('0');
    const toggleAccordion = (id: string) => {
        setOpen(open === id ? '' : id);
    };

    const checkChanges = <T,>(arr: T[], index: number, key: keyof T) =>
        index + 1 < arr.length && arr[index][key] !== arr[index + 1][key] ? 'text-danger' : '';

    const accordionProps = { toggle: toggleAccordion };

    if (!history) return <Pending />;

    return (
        <Accordion open={open} {...accordionProps}>
            <AccordionItem>
                <AccordionHeader targetId="0">
                    <Trans i18nKey="BackOfficePersonalInfo" />
                </AccordionHeader>
                <AccordionBody accordionId="0">
                    <Container className="overflow-auto">
                        {history.addresses?.length ? (
                            <Table bordered striped>
                                <thead>
                                    <tr>
                                        <th>
                                            <Trans i18nKey="BackOfficeField" />
                                        </th>
                                        {history.personalInfo.map((item, index) => (
                                            <th key={index}>
                                                <DateTimeText value={item.dt} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficePhone" />
                                        </td>
                                        {history.personalInfo.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.personalInfo,
                                                    index,
                                                    'phoneNumber'
                                                )}
                                                key={index}
                                            >
                                                <PhoneNumber value={item.phoneNumber} />
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeAdditionalPhoneNumber" />
                                        </td>
                                        {history.personalInfo.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.personalInfo,
                                                    index,
                                                    'additionalPhoneNumber'
                                                )}
                                                key={index}
                                            >
                                                <PhoneNumber value={item.additionalPhoneNumber} />
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeLastName" />
                                        </td>
                                        {history.personalInfo.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.personalInfo,
                                                    index,
                                                    'lastName'
                                                )}
                                                key={index}
                                            >
                                                {item.lastName}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeFirstName" />
                                        </td>
                                        {history.personalInfo.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.personalInfo,
                                                    index,
                                                    'firstName'
                                                )}
                                                key={index}
                                            >
                                                {item.firstName}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeMiddleName" />
                                        </td>
                                        {history.personalInfo.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.personalInfo,
                                                    index,
                                                    'middleName'
                                                )}
                                                key={index}
                                            >
                                                {item.middleName}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeLastName" />
                                        </td>
                                        {history.personalInfo.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.personalInfo,
                                                    index,
                                                    'lastName'
                                                )}
                                                key={index}
                                            >
                                                {item.lastName}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeSecondLastName" />
                                        </td>
                                        {history.personalInfo.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.personalInfo,
                                                    index,
                                                    'secondLastName'
                                                )}
                                                key={index}
                                            >
                                                {item.secondLastName}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeBirthday" />
                                        </td>
                                        {history.personalInfo.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.personalInfo,
                                                    index,
                                                    'birthday'
                                                )}
                                                key={index}
                                            >
                                                {sharpDateFormatter(item.birthday, dateFormat)}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeOperator" />
                                        </td>
                                        {history.personalInfo.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.personalInfo,
                                                    index,
                                                    'operatorFio'
                                                )}
                                                key={index}
                                            >
                                                {item.operatorFio}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </Table>
                        ) : (
                            <NoData />
                        )}
                    </Container>
                </AccordionBody>
            </AccordionItem>
            <AccordionItem>
                <AccordionHeader targetId="1">
                    <Trans i18nKey="BackOfficeDocNum" />
                </AccordionHeader>
                <AccordionBody accordionId="1">
                    <Container className="overflow-auto">
                        {history.docNum?.length ? (
                            <Table bordered striped>
                                <thead>
                                    <tr>
                                        <th>
                                            <Trans i18nKey="BackOfficeField" />
                                        </th>
                                        {history.docNum.map((item, index) => (
                                            <th key={index}>
                                                <DateTimeText value={item.dt} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeType" />
                                        </td>
                                        {history.docNum.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.docNum,
                                                    index,
                                                    'docType'
                                                )}
                                                key={index}
                                            >
                                                {item.docType}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeDocNum" />
                                        </td>
                                        {history.docNum.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.docNum,
                                                    index,
                                                    'docNum'
                                                )}
                                                key={index}
                                            >
                                                {item.docNum}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeOperator" />
                                        </td>
                                        {history.docNum.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.docNum,
                                                    index,
                                                    'operatorFio'
                                                )}
                                                key={index}
                                            >
                                                {item.operatorFio}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </Table>
                        ) : (
                            <NoData />
                        )}
                    </Container>
                </AccordionBody>
            </AccordionItem>
            <AccordionItem>
                <AccordionHeader targetId="2">
                    <Trans i18nKey="BackOfficeActualAddressAndMaritalStatus" />
                </AccordionHeader>
                <AccordionBody accordionId="2">
                    <Container className="overflow-auto">
                        {history.addresses?.length ? (
                            <Table bordered striped>
                                <thead>
                                    <tr>
                                        <th>
                                            <Trans i18nKey="BackOfficeField" />
                                        </th>
                                        {history.addresses.map((item, index) => (
                                            <th key={index}>
                                                <DateTimeText value={item.dt} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemAddressElementsCountry" />
                                        </td>
                                        {history.addresses.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.addresses,
                                                    index,
                                                    'countryName'
                                                )}
                                                key={index}
                                            >
                                                {item.countryName}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemAddressElementsRegion" />
                                        </td>
                                        {history.addresses.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.addresses,
                                                    index,
                                                    'regionName'
                                                )}
                                                key={index}
                                            >
                                                {item.regionName}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemAddressElementsDistrict" />
                                        </td>
                                        {history.addresses.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.addresses,
                                                    index,
                                                    'districtName'
                                                )}
                                                key={index}
                                            >
                                                {item.districtName}
                                            </td>
                                        ))}
                                    </tr>

                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeAddress" />
                                        </td>
                                        {history.addresses.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.addresses,
                                                    index,
                                                    'address'
                                                )}
                                                key={index}
                                            >
                                                {item.address}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeTypeOfAccommodation" />
                                        </td>
                                        {history.addresses.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.addresses,
                                                    index,
                                                    'typeOfAccommodation'
                                                )}
                                                key={index}
                                            >
                                                {item.typeOfAccommodation}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeTimeOfCurrentResidence" />
                                        </td>
                                        {history.addresses.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.addresses,
                                                    index,
                                                    'timeOfCurrentResidence'
                                                )}
                                                key={index}
                                            >
                                                {item.timeOfCurrentResidence}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeMaritalStatus" />
                                        </td>
                                        {history.addresses.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.addresses,
                                                    index,
                                                    'maritalStatus'
                                                )}
                                                key={index}
                                            >
                                                {item.maritalStatus}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeNumberOfDependents" />
                                        </td>
                                        {history.addresses.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.addresses,
                                                    index,
                                                    'numberOfDependents'
                                                )}
                                                key={index}
                                            >
                                                {item.numberOfDependents}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeNumberOfChildren" />
                                        </td>
                                        {history.addresses.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.addresses,
                                                    index,
                                                    'numberOfChildren'
                                                )}
                                                key={index}
                                            >
                                                {item.numberOfChildren}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeOperator" />
                                        </td>
                                        {history.addresses.map((item, index) => (
                                            <td key={index}>{item.operatorFio}</td>
                                        ))}
                                    </tr>
                                </tbody>
                            </Table>
                        ) : (
                            <NoData />
                        )}
                    </Container>
                </AccordionBody>
            </AccordionItem>
            <AccordionItem>
                <AccordionHeader targetId="3">
                    <Trans i18nKey="BackOfficeIncomeEmploymentBankDetails" />
                </AccordionHeader>
                <AccordionBody accordionId="3">
                    <Container className="overflow-auto">
                        {history.employment?.length ? (
                            <Table bordered striped>
                                <thead>
                                    <tr>
                                        <th>
                                            <Trans i18nKey="BackOfficeField" />
                                        </th>
                                        {history.employment.map((item, index) => (
                                            <th key={index}>
                                                <DateTimeText value={item.dt} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeIncome" />
                                        </td>
                                        {history.employment.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.employment,
                                                    index,
                                                    'income'
                                                )}
                                                key={index}
                                            >
                                                {item.income}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeMonthlyExpenses" />
                                        </td>
                                        {history.employment.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.employment,
                                                    index,
                                                    'monthlyExpenses'
                                                )}
                                                key={index}
                                            >
                                                {item.monthlyExpenses}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeSocialStatus" />
                                        </td>
                                        {history.employment.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.employment,
                                                    index,
                                                    'socialStatus'
                                                )}
                                                key={index}
                                            >
                                                {item.socialStatus}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeWorkingIndustry" />
                                        </td>
                                        {history.employment.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.employment,
                                                    index,
                                                    'workingIndustry'
                                                )}
                                                key={index}
                                            >
                                                {item.workingIndustry}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeWorkExperience" />
                                        </td>
                                        {history.employment.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.employment,
                                                    index,
                                                    'workExperience'
                                                )}
                                                key={index}
                                            >
                                                {item.workExperience}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeCompanyName" />
                                        </td>
                                        {history.employment.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.employment,
                                                    index,
                                                    'companyName'
                                                )}
                                                key={index}
                                            >
                                                {item.companyName}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeWorkPhone" />
                                        </td>
                                        {history.employment.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.employment,
                                                    index,
                                                    'workPhone'
                                                )}
                                                key={index}
                                            >
                                                {item.workPhone}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeGuarantorName" />
                                        </td>
                                        {history.employment.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.employment,
                                                    index,
                                                    'guarantorName'
                                                )}
                                                key={index}
                                            >
                                                {item.guarantorName}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="DictItemClientMoreDataTypeGuarantorPhone" />
                                        </td>
                                        {history.employment.map((item, index) => (
                                            <td
                                                className={checkChanges(
                                                    history.employment,
                                                    index,
                                                    'guarantorPhone'
                                                )}
                                                key={index}
                                            >
                                                {item.guarantorPhone}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>
                                            <Trans i18nKey="BackOfficeOperator" />
                                        </td>
                                        {history.employment.map((item, index) => (
                                            <td key={index}>{item.operatorFio}</td>
                                        ))}
                                    </tr>
                                </tbody>
                            </Table>
                        ) : (
                            <NoData />
                        )}
                    </Container>
                </AccordionBody>
            </AccordionItem>
            <AccordionItem>
                <AccordionHeader targetId="4">
                    <Trans i18nKey="BackOfficeClientBlocks" />
                </AccordionHeader>
                <AccordionBody accordionId="4">
                    <Container className="overflow-auto">
                        {history.blocks?.length ? (
                            <Table bordered striped>
                                <thead>
                                    <tr>
                                        <th>
                                            <Trans i18nKey="BackOfficeAction" />
                                        </th>
                                        <th>
                                            <Trans i18nKey="BackOfficePeriod" />
                                        </th>
                                        <th>
                                            <Trans i18nKey="BackOfficeClientBlockSd" />
                                        </th>
                                        <th>
                                            <Trans i18nKey="BackOfficeClientBlockEd" />
                                        </th>
                                        <th>
                                            <Trans i18nKey="BackOfficeAdminComment" />
                                        </th>
                                        <th>
                                            <Trans i18nKey="BackOfficeUserComment" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.blocks.map((block, index) => (
                                        <tr key={index}>
                                            <td>
                                                <Trans
                                                    i18nKey={
                                                        'DictItemClientBlockActions' + block.action
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <Trans
                                                    i18nKey={
                                                        'DictItemClientBlockPeriods' + block.period
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <DateTimeText value={block.sd} />
                                            </td>
                                            <td>
                                                <DateTimeText value={block.ed} />
                                            </td>
                                            <td>{block.adminComment}</td>
                                            <td>{block.userComment}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <NoData />
                        )}
                    </Container>
                </AccordionBody>
            </AccordionItem>
            {collectionStatus && (
                <AccordionItem>
                    <AccordionHeader targetId="5">
                        <Trans i18nKey="BackOfficeCollectionStatus" />
                    </AccordionHeader>
                    <AccordionBody accordionId="5">
                        <Container className="overflow-auto">
                            {history.collectionStatuses?.length ? (
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>
                                                <Trans i18nKey="BackOfficeCollectionStatus" />
                                            </th>
                                            <th>
                                                <Trans i18nKey="BackOfficeDate" />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.collectionStatuses.map((cs, index) => (
                                            <tr key={index}>
                                                <td>{cs.collectionStatus}</td>
                                                <td>
                                                    <DateTimeText value={cs.sd} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <NoData />
                            )}
                        </Container>
                    </AccordionBody>
                </AccordionItem>
            )}
        </Accordion>
    );
};

export default HistoryTab;
